<script setup lang="ts">
const { t } = useL10n()
const { isLoading } = useGlobalState()
const { pageTitle, previousRoute, sidebarOpen } = useAppShell()

const navigateBack = () => {
  if (previousRoute.value) {
    window.history.back()
  }
}
</script>

<template>
  <header class="nav-header">
    <!-- Brand column: on desktop it is exactly as wide as the sidebar below it,
         so the divider under the logo continues the sidebar's border. -->
    <div class="nav-brand">
      <button
        type="button"
        class="menu-toggle"
        :aria-label="t('layout.nav.toggle')"
        :aria-expanded="sidebarOpen"
        @click="sidebarOpen = !sidebarOpen"
      >
        <i class="pi pi-bars" aria-hidden="true"></i>
      </button>
      <NuxtLink to="/dashboard" class="brand-link">
        <img src="/Logo.png" alt="Die Leckorter" class="brand-logo" />
      </NuxtLink>
    </div>

    <div class="nav-title-section">
      <Button
        v-if="previousRoute"
        icon="pi pi-arrow-left"
        class="back-button"
        :disabled="isLoading"
        :aria-label="t('global.back')"
        @click="navigateBack"
      />
      <h1 class="nav-title">{{ pageTitle }}</h1>
    </div>

    <UserMenu class="nav-user-menu" />
  </header>
</template>

<style scoped lang="scss">
.nav-header {
  position: sticky;
  top: 0;
  z-index: 95;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: var(--portal-header-height);
  padding-right: 0.75rem;
  background: var(--portal-card);
  border-bottom: 1px solid var(--portal-border);
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
  align-self: stretch;
  width: var(--portal-sidebar-width);
  // Compensates the logo's own left padding (see .brand-logo) so the wordmark
  // lines up with the sidebar icons below it.
  padding-left: 0.375rem;
  overflow: hidden;
  background: var(--portal-brand-plate);
}

.brand-link {
  display: flex;
  align-items: center;
  min-width: 0;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 4px;
    border-radius: 0.25rem;
  }
}

// Logo.png carries a wide dark margin — the wordmark fills only ~44% of the
// image height. The box is therefore sized well beyond the apparent lettering;
// the surplus is the plate colour and blends into .nav-brand.
.brand-logo {
  height: 3.25rem;
  width: auto;
  object-fit: contain;
}

.menu-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  // Sits on the dark brand plate, so it is light rather than muted grey.
  color: rgb(255 255 255 / 75%);
  cursor: pointer;

  i {
    font-size: 1.15rem;
  }

  &:hover {
    background: rgb(255 255 255 / 12%);
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -2px;
  }
}

.nav-title-section {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.nav-user-menu {
  flex-shrink: 0;
  min-width: 0;
}

.back-button {
  background: transparent !important;
  border: none !important;
  padding: 0.4rem !important;
  color: var(--portal-muted) !important;
  flex-shrink: 0;

  &:hover {
    background: var(--portal-bg) !important;
    color: var(--portal-text) !important;
  }
}

.nav-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--portal-text);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
}

@media (max-width: 900px) {
  .nav-brand {
    width: auto;
    padding: 0 0.5rem 0 0;
  }

  .menu-toggle {
    display: flex;
  }
}
</style>
